// ABOUTME: Open tickets datagrid view for the warehouse service desk.
// ABOUTME: Title pill toggles between all open tickets and mine; relation filter via gridOptions.

import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import Text from "../../../../../components/theme/text/Text";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import { ORDER_FILTER_KEYS } from "../../../../../components/global/DataGrid/Filter";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import { tokens } from "../../../../../theme";
import { localeSettings } from "../../../../../utils/locales";
import { getSupportTicketStateData } from "../../../../../utils/labelColorText";

import { useGetWarehouseOpenTicketsQuery } from "../../../../../newapi/warehouse/supportTicketApiSlice";
import MTicketDetailBottom from "../components/modals/MTicketDetailBottom";
import PriorityBadge from "../components/PriorityBadge";

const SCOPE = {
    ALL: 'all',
    MINE: 'mine',
};

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [ORDER_FILTER_KEYS.RELATION],
};

const TypeBadge = ({ type }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    if (!type) return null;
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: generateColorFromTitle(type, theme.palette.mode, 90),
                borderRadius: 5,
                px: 1.5,
                py: 0.5,
                height: 24,
                lineHeight: '24px',
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: generateIconColorFromTitle(type, theme.palette.mode),
                    flexShrink: 0,
                }}
            />
            <Text bold sx={{ fontSize: 13 }}>{t(type)}</Text>
        </Box>
    );
};

const StatusChip = ({ status }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    if (!status) return null;
    const { labelcolor, labeltext } = getSupportTicketStateData(status, theme);
    return (
        <ShLabel
            fitted
            palette="normal"
            size={24}
            variant={labelcolor}
            sx={{ lineHeight: "24px" }}
        >
            {t(labeltext)}
        </ShLabel>
    );
};

const ScopeToggle = ({ scope, setScope }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={(_, v) => v && setScope(v)}
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                borderRadius: 5,
                p: 0.5,
                gap: 0.5,
                height: 40,
                "& .MuiToggleButtonGroup-grouped": {
                    border: 0,
                    borderRadius: "12px !important",
                    whiteSpace: "nowrap",
                    flex: 1,
                    textTransform: 'none',
                    borderRadius: 5,
                    fontSize: 14,
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    color: `${colors.txt['secondary']} !important`,
                    px: 2,
                    color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                    "&.Mui-selected": {
                        color: colors.txt['primary'],
                        fontWeight: 600,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? "none" : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? "none" : `0px 2px 4px ${colors.grey[200]}`,
                        color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"],
                    },
                    "&:hover": {
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                    },
                },
            }}
        >
            <ToggleButton value={SCOPE.ALL}>{t('all_open_tickets')}</ToggleButton>
            <ToggleButton value={SCOPE.MINE}>{t('your_open_tickets')}</ToggleButton>
        </ToggleButtonGroup>
    );
};

const OpenTickets = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const dateLocale = localeSettings[i18n.language]?.dateLocale;

    const [scope, setScope] = useState(SCOPE.ALL);

    // Body for the open-tickets endpoint. `mineOnly` flips with the toggle;
    // `relationId: null` is the marker key so ShDataGrid2 doesn't bail on an
    // empty body before the relation filter ever fires.
    const [config, setConfig] = useState({
        mineOnly: false,
        relationId: null,
    });

    useEffect(() => {
        setConfig((prev) => ({ ...prev, mineOnly: scope === SCOPE.MINE }));
    }, [scope]);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions,
        });
    }, []);

    const [detailTicketId, setDetailTicketId] = useState(null);
    const detailOpen = detailTicketId !== null;
    const closeTicketDetail = () => setDetailTicketId(null);

    const onRowClick = (row) => {
        if (!row?.row?.id) return;
        setDetailTicketId(row.row.id);
    };

    const columns = useMemo(() => [
        {
            field: "title",
            headerName: t('name'),
            flex: 2,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return (
                    <Box
                        sx={{
                            display:"flex",
                            alignItems:"center",
                            gap:1.5,
                        }}
                    >
                        <PriorityBadge
                            priority={params.row.priority}
                        />
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                            }}
                        >
                            <Text noBreak>{params.row.title}</Text>
                            <Text light bold>{params.row.ticketNumber}</Text>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "relation",
            headerName: t('created_by'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Text>{params.row.relation?.name || '—'}</Text>
                        <Text light sx={{ fontSize: 12 }}>
                            {params.row.relation?.contactName || ''}
                        </Text>
                    </Box>
                );
            },
        },
        {
            field: "type",
            headerName: t('type'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <TypeBadge type={params.row.type} />;
            },
        },
        {
            field: "status",
            headerName: t('status'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <StatusChip status={params.row.status} />;
            },
        },
        {
            field: "assignee",
            headerName: t('assigned_to'),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <Text>{params.row.assignee?.name || '—'}</Text>;
            },
        },
        {
            field: "updatedAt",
            headerName: t('last_update'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                if (!params.row.updatedAt) return <Text light>—</Text>;
                return (
                    <Text light>
                        {formatDistanceToNowStrict(new Date(params.row.updatedAt), {
                            addSuffix: true,
                            locale: dateLocale,
                        })}
                    </Text>
                );
            },
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 1, py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text bold>#{params.row.ticketNumber}</Text>
                            <TypeBadge type={params.row.type} />
                        </Box>
                        <Text noBreak>{params.row.title}</Text>
                        <Text light sx={{ fontSize: 12 }}>
                            {params.row.relation?.name || '—'}
                        </Text>
                    </Box>
                );
            },
        },
    ], [t, dateLocale]);

    const defaultSort = useMemo(() => ({ field: 'updatedAt', type: 'DESC' }), []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 1 }}>
            <ShDataGrid2
                key={theme.palette.mode}
                content={useGetWarehouseOpenTicketsQuery}
                config={config}
                title={<ScopeToggle scope={scope} setScope={setScope} />}
                gridOptions={gridOptions}
                sortModel={defaultSort}
                columns={columns}
                onRowClick={onRowClick}
            />

            <MTicketDetailBottom
                open={detailOpen}
                onClose={closeTicketDetail}
                ticketId={detailTicketId}
                viewer="fulfilmentcenter"
            />
        </Box>
    );
};

export default OpenTickets;
